import { useEffect, useState } from "react";
import { useAccessibilityTags, useFilters, useI18N } from "common/hooks";
import Checkbox from "@/components/common/Checkbox";
import AnalyticsService from "@/services/analytics";
import { StandardButton } from "@/components/common/StandardButton";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/shadcn/components/ui/accordion";
import { Divide } from "lucide-react";

interface CollapseProps {
  label: string;
  tagsToShow: any[];
}

function AccessibilityCollapse(props: CollapseProps) {
  const i18n = useI18N();

  const [open, setOpen] = useState<string | null>(null);
  const { filtersState, setFiltersState } = useFilters();
  const { tagsToAdd } = useAccessibilityTags();

  const [allSubItemsSelected, setAllSubItemsSelected] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { tagsToShow } = props;

  useEffect(() => {
    if (filtersState.accessibilityOptions.includes(428)) {
      setAllSubItemsSelected(
        tagsToAdd.every((tag) =>
          filtersState.accessibilityOptions.includes(tag.id),
        ),
      );
    } else {
      setAllSubItemsSelected(false);
    }
  }, [tagsToAdd, filtersState.accessibilityOptions]);

  const setSelectedOptions = (options: number) => {
    // Add it
    if (!filtersState.accessibilityOptions.includes(options)) {
      AnalyticsService.trackGAEventHandler("homeSelectedFiltersToAdd");
      AnalyticsService.trackMx("homeSelectedFiltersToAdd", {
        FilterTag: "businessShowOption_" + options,
      });

      setFiltersState((prev) => ({
        ...prev,
        accessibilityOptions: [...prev.accessibilityOptions, options],
      }));
    } else {
      // remove it
      AnalyticsService.trackGAEventHandler("homeSelectedFiltersToRemove");
      AnalyticsService.trackMx("homeSelectedFiltersToRemove", {
        FilterTag: "businessShowOption_" + options,
      });

      if (options === 428) {
        const ids = [428, 964, 844, 553, 554, 753];

        // When unchecking"All", remove all tags from tagsToAdd but keep others
        setAllSubItemsSelected(false);
        const tagsIdsSet = new Set(ids);

        setFiltersState((prev) => ({
          ...prev,
          accessibilityOptions: prev.accessibilityOptions.filter(
            (option) => !tagsIdsSet.has(option),
          ),
        }));
      } else {
        setFiltersState((prev) => ({
          ...prev,
          accessibilityOptions: prev.accessibilityOptions.filter(
            (o) => o !== options,
          ),
        }));
      }
    }
  };

  const isSelected = (id: number) => {
    return filtersState.accessibilityOptions.includes(id);
  };

  const isAnyOneSelected = () => {
    const currentIds = tagsToShow.map((o) => o.id);
    return filtersState.accessibilityOptions.some((id) =>
      currentIds.includes(id),
    );
  };

  const actualTagsToShow = showAll ? tagsToShow : tagsToShow.slice(0, 5);

  return (
    <Accordion
      type="single"
      collapsible
      value={open}
      onValueChange={(value) => setOpen(value as string)}
      className="w-full"
    >
      <AccordionItem value="accessibility-filters" className="border-none">
        <AccordionTrigger
          tabIndex={0}
          aria-label={`${open === "accessibility-filters" ? "Close" : "Open"} ${props.label} accessibility filters`}
          className={`p-4 flex items-center cursor-pointer focus-blue ${
            !isAnyOneSelected()
              ? "bg-container-brand-minimal"
              : "bg-container-brand-minimal"
          }`}
        >
          <div className="text-fg-neutral-strong flex-grow body-l-semibold text-left">
            {props.label}
          </div>
        </AccordionTrigger>
        <AccordionContent className="overflow-visible">
          <ul className="p-4 flex flex-col gap-4" role="none">
            {actualTagsToShow.map((o) => {
              return (
                <div key={o.id}>
                  <Checkbox
                    checked={filtersState.accessibilityOptions?.includes(o.id)}
                    key={o.id}
                    onCheck={() => setSelectedOptions(o.id)}
                    label={o.name}
                    tabIndex={open === "accessibility-filters" ? 0 : -1}
                  />
                  {o.id === 428 &&
                    filtersState.accessibilityOptions?.includes(o.id) && (
                      <div key={o.id + "321"}>
                        <p className="mt-2 ml-6">
                          {i18n.get("wouldAnyOfTheseAlsoWork")}
                        </p>
                        <Checkbox
                          className="mt-2 ml-6"
                          key="all"
                          label="All"
                          checked={allSubItemsSelected}
                          onCheck={() => {
                            if (allSubItemsSelected) {
                              setFiltersState((prev) => ({
                                ...prev,
                                accessibilityOptions:
                                  prev.accessibilityOptions.filter(
                                    (o) =>
                                      !tagsToAdd.map((o) => o.id).includes(o),
                                  ),
                              }));
                            } else {
                              setFiltersState((prev) => ({
                                ...prev,
                                accessibilityOptions: [
                                  ...prev.accessibilityOptions,
                                  ...tagsToAdd.map((o) => o.id),
                                ],
                              }));
                            }
                            setAllSubItemsSelected(!allSubItemsSelected);
                          }}
                          tabIndex={open === "accessibility-filters" ? 0 : -1}
                        />
                        {tagsToAdd.map((tag) => (
                          <Checkbox
                            className="mt-2 ml-12"
                            key={tag.id}
                            label={tag.name}
                            checked={isSelected(tag.id)}
                            onCheck={() => {
                              if (isSelected(tag.id)) {
                                setAllSubItemsSelected(false);
                              }
                              setSelectedOptions(tag.id);
                            }}
                            tabIndex={open === "accessibility-filters" ? 0 : -1}
                          />
                        ))}
                      </div>
                    )}
                </div>
              );
            })}
          </ul>
          {tagsToShow.length > 5 && (
            <StandardButton
              tabIndex={open === "accessibility-filters" ? 0 : -1}
              onClick={() => {
                setShowAll(!showAll);
              }}
              className="ml-4 mb-4"
              variant="blueLink"
              size="sm"
            >
              {!showAll ? i18n.get("showMore") : i18n.get("showLess")}
            </StandardButton>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default AccessibilityCollapse;
